import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * Last line of defence against a white screen.
 *
 * A render error anywhere below this unmounts the whole tree, and React's
 * default for that is a completely blank page — which tells the player nothing
 * and tells us nothing either. This catches it, shows something human, and
 * surfaces the actual message so a bug report can contain a fact instead of
 * "it didn't work".
 */
interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Kept so the stack survives in the console even though the tree is gone.
    console.error("Badly Dubbed crashed:", error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="bd-screen">
        <main className="shell bd-stack-lg" style={{ justifyContent: "center" }}>
          <div className="plate bd-pad-lg center bd-stack-lg">
            <h1 className="t-display t-outlined" style={{ fontSize: 44 }}>
              Broke
            </h1>
            <p className="t-body">
              Something in the app fell over. Your room is fine &mdash; reloading
              usually picks it straight back up.
            </p>
            <button className="btn btn-submit" onClick={() => window.location.reload()}>
              Reload
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => {
                // The nuclear option: a corrupt saved seat can crash the app on
                // every load, and then Reload alone never escapes it.
                try {
                  window.localStorage.removeItem("badly-dubbed.session");
                } catch {
                  /* Storage blocked; the reload is still worth trying. */
                }
                window.location.href = window.location.pathname;
              }}
            >
              Leave the room and start over
            </button>
            <p className="t-micro muted" style={{ wordBreak: "break-word" }}>
              {error.message}
            </p>
          </div>
        </main>
      </div>
    );
  }
}
